import { Alert, Button, Modal, Space, Spin, Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ReportAnswerState } from '../useReportAnswer';
import { getReportPlatformName, safeReportUrl } from '../reportAnswer';
import styles from './ReportAnswerModal.module.less';

/** 共享弹窗属性，三种报表入口使用同一加载与失效逻辑。 */
interface ReportAnswerModalProps {
  state: ReportAnswerState;
  onClose: () => void;
  onRetry: () => void;
  platformList: { platform: string; name: string }[];
}

/**
 * 使用阅读型分区展示问题、平台名称和保存的回答，保留 Markdown 的安全渲染规则。
 * @param {ReportAnswerModalProps} props 当前回答状态、操作回调和平台字典。
 * @returns {JSX.Element} 可在桌面、移动和嵌入页面共用的只读弹窗。
 */
export default function ReportAnswerModal({ state, onClose, onRetry, platformList }: ReportAnswerModalProps): JSX.Element {
  const data = state.data;
  const platformName = data ? getReportPlatformName(data.platform, platformList) : '';
  return (
    <Modal
      title="模型回答"
      open={state.open}
      onCancel={onClose}
      footer={null}
      width={880}
      rootClassName={styles.root}
      className={styles.modal}
      classNames={{ container: styles.container, header: styles.header, title: styles.title, body: styles.body }}
      destroyOnHidden
    >
      <div className={styles.content}>
        {state.status === 'loading' && <div role="status" className={styles.loading}><Spin /><span>回答加载中...</span></div>}
        {state.status === 'network-error' && <Space orientation="vertical"><Alert type="error" title="回答加载失败，请重试" /><Button onClick={onRetry}>重试</Button></Space>}
        {state.status === 'unavailable' && <Alert type="warning" title="当前回答不可查看或数据已更新，请刷新后重试" />}
        {state.status === 'success' && data && <>
          {/* 标签和问题分列，平台不收缩，长问题在剩余宽度内自然换行。 */}
          <div className={styles.questionHeader}>
            <Tag className={styles.platform}>{platformName}</Tag>
            <Typography.Title level={5} className={styles.question}>{data.question}</Typography.Title>
          </div>
          {/* 标题栏在滚动区外，问题与回答在同一内容区滚动，避免长问题遮挡正文。 */}
          <section className={styles.answerSection} aria-label="回答内容">
            <h3 className={styles.answerTitle}>回答内容</h3>
            <div className={`${styles.markdown} markdown-body`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml urlTransform={safeReportUrl}
                components={{
                  /**
                   * 回答链接始终在隔离的新标签页打开，仅保留安全 HTTP 链接。
                   * @param {import('react').ComponentProps<'a'>} props Markdown 转换后的锚点属性。
                   * @returns {JSX.Element} 安全链接，危险地址仅保留文本。
                   */
                  a: ({ href, children }) => safeReportUrl(href)
                    ? <a href={safeReportUrl(href)} target="_blank" rel="noopener noreferrer">{children}</a>
                    : <span>{children}</span>,
                  /**
                   * 模型回答不自动加载远程图片，以免向外部地址发送公开报告访问信息。
                   * @param {import('react').ComponentProps<'img'>} props Markdown 图片描述。
                   * @returns {JSX.Element} 图片替代文本。
                   */
                  img: ({ alt }) => <span>{alt || ''}</span>,
                }}
              >{data.answer}</ReactMarkdown>
            </div>
          </section>
        </>}
      </div>
    </Modal>
  );
}
